/**
 * Fullscreen API with the webkit-prefixed fallbacks (iPadOS Safari, older Safari), plus the checks
 * that decide whether the Player's fullscreen button makes sense. Written against small structural
 * types so node:test can drive it with fake documents. iPhone Safari has no fullscreen API for
 * non-video elements: there `canFullscreen` is false and the button stays hidden.
 */

export interface FullscreenDocument {
  fullscreenEnabled?: boolean;
  webkitFullscreenEnabled?: boolean;
  fullscreenElement?: Element | null;
  webkitFullscreenElement?: Element | null;
  exitFullscreen?(): Promise<void> | void;
  webkitExitFullscreen?(): Promise<void> | void;
}

export interface FullscreenTarget {
  requestFullscreen?(): Promise<void> | void;
  webkitRequestFullscreen?(): Promise<void> | void;
}

/** Both spellings; listen to each on `document`. */
export const FULLSCREEN_EVENTS = ["fullscreenchange", "webkitfullscreenchange"] as const;

/** The element showing fullscreen right now, if any. */
export function fullscreenElementOf(doc: FullscreenDocument): Element | null {
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

export function isFullscreen(doc: FullscreenDocument): boolean {
  return fullscreenElementOf(doc) !== null;
}

/** True when the element can be put on fullscreen here (an API exists and the document allows it). */
export function canFullscreen(doc: FullscreenDocument, target: FullscreenTarget): boolean {
  const hasRequest = typeof target.requestFullscreen === "function" || typeof target.webkitRequestFullscreen === "function";
  if (!hasRequest) return false;
  if (doc.fullscreenEnabled === false) return false;
  // Only the prefixed flag exists on old Safari; the standard one wins when present.
  if (doc.fullscreenEnabled === undefined && doc.webkitFullscreenEnabled === false) return false;
  return true;
}

/** Must run inside the click handler (user gesture). Resolves false if the browser refused. */
export async function enterFullscreen(target: FullscreenTarget): Promise<boolean> {
  try {
    if (typeof target.requestFullscreen === "function") await target.requestFullscreen();
    else if (typeof target.webkitRequestFullscreen === "function") await target.webkitRequestFullscreen();
    else return false;
    return true;
  } catch {
    return false;
  }
}

export async function exitFullscreen(doc: FullscreenDocument): Promise<boolean> {
  try {
    if (typeof doc.exitFullscreen === "function") await doc.exitFullscreen();
    else if (typeof doc.webkitExitFullscreen === "function") await doc.webkitExitFullscreen();
    else return false;
    return true;
  } catch {
    return false;
  }
}

/** The bits of `window` that tell whether the page already runs as an installed app. */
export interface DisplayModeWindow {
  matchMedia?(query: string): { matches: boolean };
  navigator?: object; // iOS adds `standalone`, which is not in the DOM typings
}

/** Installed PWA or Add to Home Screen: standalone / fullscreen display mode, or iOS `navigator.standalone`. */
export function isInstalledApp(win: DisplayModeWindow): boolean {
  if ((win.navigator as { standalone?: boolean } | undefined)?.standalone === true) return true;
  if (typeof win.matchMedia !== "function") return false;
  try {
    return ["standalone", "fullscreen"].some((mode) => win.matchMedia?.(`(display-mode: ${mode})`).matches === true);
  } catch {
    return false;
  }
}

/**
 * Whether the fullscreen button is shown: the API exists and the app is not already installed
 * standalone / fullscreen (`installed` is read once at startup, before any fullscreen we request,
 * because `(display-mode: fullscreen)` also matches while the Fullscreen API is active).
 */
export function showFullscreenButton(doc: FullscreenDocument, target: FullscreenTarget, installed: boolean): boolean {
  return !installed && canFullscreen(doc, target);
}
